import { APP, DEFAULT_DOC_NAME, FontFamilyValue } from '@/constants';
import { RecipientType, RecipientSettings } from '@/types/types';
import { Location } from 'react-router-dom';
import i18n from 'i18next';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';

dayjs.extend(utc);
dayjs.extend(timezone);

export const toCamelCase = (text: string) => {
    const firstLetter = text.charAt(0);
    const rest = text.slice(1);

    return firstLetter.toUpperCase() + rest.toLowerCase();
};

export const isEmailValid = (email: string) => {
    if (/[^\x20-\x7E]/.test(email)) return false;
    // eslint-disable-next-line no-useless-escape
    return /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(
        email
    );
};

export const isPasswordValid = (password: string) => {
    return password.length > 7;
};

export function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function isDuplicate(value: string, array: string[]) {
    if (!value) return false;
    return array.filter((item) => item === value).length > 1;
}

export function areEmailsValid(array: string[]) {
    return array.every((email) => email === '' || isEmailValid(email));
}

export function isNonEmptyString(value: string) {
    return Boolean(value.replaceAll(' ', ''));
}

export function isEmptyString(value: string) {
    return !isNonEmptyString(value);
}

export function isValidRecipient({
    type,
    value,
}: {
    type: RecipientType;
    value: string;
}) {
    if (type === 'EMAIL') return isEmailValid(value);

    if (type === 'PHONE') return isPhoneNumberValid(value);

    if (type === 'SIGN_LINK_ONLY') return isRecipientNameValid(value);

    return false;
}

export function hasValidRecipients({
    recipients,
    recipientsTypes,
    recipientsNames,
    recipientsSettings,
}: {
    recipients: string[];
    recipientsTypes: RecipientType[];
    recipientsNames: string[];
    recipientsSettings: RecipientSettings[];
}) {
    const hasNonEmptySigners = recipients.some(
        (r, i) => isNonEmptyString(r) && !recipientsSettings[i].isCopyOnly
    );

    if (!hasNonEmptySigners) return false;

    return recipients.every((r, i) => {
        if (isEmptyString(r)) return true;

        return isValidRecipient({ value: r, type: recipientsTypes[i] });
    });
}

export function isRecipientNameValid(value: string = '') {
    return value.trim().length > 2;
}

export function isPhoneRecipientValid(phone: string, name: string) {
    return isPhoneNumberValid(phone) && isRecipientNameValid(name);
}

export function hasNoDuplicates(array: string[]) {
    return !array.some((element, index) => {
        return element !== '' && array.indexOf(element) !== index;
    });
}

export function trimExtension(str: string) {
    return str.replace(/\.[^/.]+$/, '');
}

export function getFileExtension(filename: string) {
    const extension = filename.split('.').pop();
    return extension?.toLocaleLowerCase();
}

export const getTextWidth = (
    text: string,
    fontSize: number,
    fontFamily: FontFamilyValue
) => {
    const canvas = new OffscreenCanvas(1, 1);
    const ctx = canvas.getContext('2d')!;
    ctx.font = `${fontSize}px ${fontFamily}`;

    let width;

    // Only apply improved logic for monospace fonts
    if (fontFamily.includes('monospace')) {
        // Add a non-breaking space to better account for trailing/leading spaces
        const widthNormal = ctx.measureText(text).width;
        const widthNbsp = ctx.measureText(text + '\u00A0').width;
        width = Math.max(widthNormal, widthNbsp);
    } else {
        width = ctx.measureText(text).width + 10; // Add a small buffer to avoid clipping
    }

    return width;
};

export const replaceMultipleSpaces = (string: string) => {
    return string.replace(/\s+/g, ' ');
};

export class LocationUrl {
    private pathname: string;
    private search: string;

    constructor({ pathname, search }: Location) {
        this.pathname = pathname;
        this.search = search;
    }

    get url() {
        return this.search ? `${this.pathname}${this.search}` : this.pathname;
    }

    upsertParam(name: string, value: string) {
        let params = new URLSearchParams(this.search);
        params.delete(name);
        params.append(name, value);
        this.search = `?${params.toString()}`;
    }
}

export const getPrevLocationUrl = (location: Location) => {
    const { prevLocation } = location.state as { prevLocation: Location };
    return new LocationUrl(prevLocation);
};

export const toDocName = (docUrl: string) => {
    const slashIndex = docUrl.lastIndexOf('/');
    const underscoreIndex = docUrl.lastIndexOf('__');

    const fileName = docUrl.slice(slashIndex + 1, underscoreIndex) + '.pdf';

    return fileName;
};

export const toFileName = (fileUrl: string) => {
    const slashIndex = fileUrl.lastIndexOf('/');
    const underscoreIndex = fileUrl.lastIndexOf('__');

    const fileName =
        fileUrl.slice(slashIndex + 1, underscoreIndex) +
        `.${getFileExtension(fileUrl)}`;

    return fileName;
};

export const pathToFileName = (path: string) => {
    const slashIndex = path.lastIndexOf('/');

    return path.slice(slashIndex + 1);
};

export function isValidUrl(string: string) {
    try {
        new URL(string);
        return true;
    } catch (err) {
        return false;
    }
}

export const isValidRoNumber = (phoneNumber: string) => {
    const onlyNumberRegex = new RegExp('^[0-9]+$');
    return (
        phoneNumber.length === 10 &&
        phoneNumber.startsWith('07') &&
        onlyNumberRegex.test(phoneNumber)
    );
};

export const isValidInterNumber = (phoneNumber: string) => {
    const onlyNumberRegex = new RegExp('^[0-9]+$');

    const numbers = phoneNumber.slice(1);

    return (
        phoneNumber.startsWith('+') &&
        numbers.length > 8 &&
        numbers.length < 14 &&
        onlyNumberRegex.test(numbers)
    );
};

export const isPhoneNumberValid = (number: string) => {
    return isValidRoNumber(number) || isValidInterNumber(number);
};

export const textToCsvDownload = (text: string) => {
    const link = document.createElement('a');
    const file = new Blob([text], { type: 'text/plain' });
    link.href = URL.createObjectURL(file);
    link.download = 'table.csv';
    link.click();
    URL.revokeObjectURL(link.href);
};

export const textToFileDownload = ({
    text,
    fileName,
    ext = 'txt',
}: {
    text: string;
    fileName: string;
    ext?: string;
}) => {
    const link = document.createElement('a');
    const file = new Blob([text], { type: 'text/plain' });
    link.href = URL.createObjectURL(file);
    link.download = `${fileName}.${ext}`;
    link.click();
    URL.revokeObjectURL(link.href);
};

export const hasDuplicates = (arr: string[]) =>
    new Set(arr).size !== arr.length;

export const extractFileNameFromHeader = (
    contentDispositionHeader: string | null
): string => {
    if (!contentDispositionHeader) return DEFAULT_DOC_NAME;

    const regExpFilename = /filename="(?<filename>.*)"/;

    return (
        regExpFilename.exec(contentDispositionHeader)?.groups?.filename ??
        DEFAULT_DOC_NAME
    );
};

export const extractFilenameForCertificate = (header: string) => {
    const match = header.match(/filename="?(.*?)"?$/);
    const fileName = match ? match[1] : 'Certificate.pdf';
    return fileName;
};

export const appendSignedSuffix = (fileName: string): string => {
    const suffix = i18n.t('common:signedSuffix');
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return `${fileName}_${suffix}`;
    return `${fileName.slice(0, lastDotIndex)}_${suffix}${fileName.slice(lastDotIndex)}`;
};

export function docUrlToFileName(docUrl: string) {
    return docUrl.slice(docUrl.indexOf('/') + 1);
}

export const dayJsWithTz = (date: string) => {
    if (APP === 'ESEMNEAZA') {
        return dayjs(date).tz('Europe/Bucharest');
    } else {
        dayjs.tz.guess();
        return dayjs(date);
    }
};

export function maskPhoneNumber(phoneNumber: string) {
    // Remove non-digit characters (optional, based on your input format)
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Check if the number is long enough to mask
    if (cleaned.length < 4) {
        return cleaned; // Return the number as is if less than 4 digits
    }

    // Create the masked number, showing the last 4 digits
    const masked = cleaned.slice(0, -4).replace(/./g, '*') + cleaned.slice(-4);

    return masked;
}

/**
 * Checks if a string is a valid slug.
 * Constraints:
 *   - Only letters (A-Z, a-z), numbers (0-9), and hyphens allowed
 *   - No leading/trailing hyphens
 *   - No consecutive hyphens
 *   - Example valid: 'MySlug', 'my-slug', 'another-slug-example', 'slug123', 'slug-123-test'
 *   - Example invalid: 'my--slug', '-slug', 'slug-', 'my_slug', 'my slug', 'slug@123'
 */
export function isValidSlug(str: string) {
    return !!str && /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/.test(str);
}

/**
 * Checks if a string is a valid hex color code.
 * Constraints:
 *   - Must start with '#'
 *   - 3 or 6 hexadecimal digits (0-9, a-f, A-F)
 *   - Example valid: '#fff', '#FFF', '#123abc', '#ABCDEF'
 *   - Example invalid: 'fff', '#1234', '#abcd', '#12345g', '#1234567'
 */
export function isValidHexColor(str: string) {
    return !!str && /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(str);
}

export function cleanEmail(email: string) {
    let cleanedEmail = email
        // Remove common invisible characters (ZWNJ, ZWJ, ZWSP, BOM, etc.)
        .replace(/[\u200B-\u200F\uFEFF\u2060\u00AD]/g, '')
        // Normalize accents and diacritics (e.g., é -> e)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        // Trim leading/trailing whitespace
        .trim();

    while (
        cleanedEmail.length > 1 &&
        ((cleanedEmail.startsWith("'") && cleanedEmail.endsWith("'")) ||
            (cleanedEmail.startsWith('"') && cleanedEmail.endsWith('"')) ||
            (cleanedEmail.startsWith('“') && cleanedEmail.endsWith('”')) ||
            (cleanedEmail.startsWith('‘') && cleanedEmail.endsWith('’')))
    ) {
        cleanedEmail = cleanedEmail.slice(1, -1).trim();
    }

    return (
        cleanedEmail
            .replaceAll(' ', '')
            // Normalize casing (most email systems are case-insensitive)
            .toLowerCase()
    );
}

export function getMaxFontSizeToFit(
    ctx: CanvasRenderingContext2D,
    text: string,
    fontFamily: string,
    targetHeight: number
) {
    let fontSize = targetHeight;
    let metrics;
    while (fontSize > 0) {
        ctx.font = `${fontSize}px ${fontFamily}`;
        metrics = ctx.measureText(text);
        const totalHeight =
            metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
        if (totalHeight <= targetHeight) break;
        fontSize -= 1;
    }
    return fontSize;
}

/**
 * Returns the maximum font size (in px) that fits the given text within the target height,
 * accounting for ascenders and descenders (diacritics).
 * @param text The text to measure (use a string with diacritics for best results)
 * @param fontFamily The font family to use (e.g., '"Courier Prime", monospace')
 * @param targetHeight The maximum height in px
 * @returns The maximum font size in px
 */
export function getMaxFontSizeToFitText(
    text: string,
    fontFamily: string,
    targetHeight: number
): number {
    // Create an offscreen canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    let fontSize = targetHeight;
    let metrics;
    while (fontSize > 0) {
        ctx.font = `${fontSize}px ${fontFamily}`;
        metrics = ctx.measureText(text);
        const totalHeight =
            (metrics.actualBoundingBoxAscent || fontSize) +
            (metrics.actualBoundingBoxDescent || 0);
        if (totalHeight <= targetHeight) break;
        fontSize -= 1;
    }
    return fontSize;
}

/**
 * Returns the maximum font size (in px) that fits the given text within the target width.
 * Uses directional search starting from fieldHeight for better accuracy.
 * @param text The text to measure
 * @param fontFamily The font family to use (e.g., 'Courier Prime, monospace')
 * @param targetWidth The maximum width in px
 * @param startFontSize The starting font size (typically fieldHeight)
 * @returns The maximum font size in px
 */
export function getMaxFontSizeToFitWidth(
    text: string,
    fontFamily: string,
    targetWidth: number,
    startFontSize: number
): number {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    let fontSize = Math.max(1, Math.floor(startFontSize));
    ctx.font = `${fontSize}px ${fontFamily}`;
    let currentWidth = ctx.measureText(text).width;
    let prevGap = Math.abs(targetWidth - currentWidth);

    // Determine initial direction: increase if too narrow, decrease if too wide
    let direction = currentWidth < targetWidth ? 1 : -1;

    while (true) {
        const nextFontSize = fontSize + direction;
        if (nextFontSize < 1) break;

        ctx.font = `${nextFontSize}px ${fontFamily}`;
        const nextWidth = ctx.measureText(text).width;

        const nextGap = Math.abs(targetWidth - nextWidth);

        // If we've crossed the target or gap is increasing, we found the best size
        if (nextWidth > targetWidth && direction > 0) {
            break;
        }
        if (nextGap > prevGap) {
            break;
        }

        fontSize = nextFontSize;
        currentWidth = nextWidth;
        prevGap = nextGap;
    }

    return fontSize;
}
